/* eslint-disable import/no-unresolved */
import BaseElement from 'base-element'
import { css, html, createRef, ref, styleMap } from 'lit-all'
import { formatMoney, addToCart, handleize } from 'lib-utils'
import styles from 'lib-styles'
/* eslint-disable import/no-unresolved */

class HHProductBundlePDP extends BaseElement {
  static properties = {
    products: {
      type: Array,
    },
    title: {
      type: String,
    },
    showFinalSaleText: {
      type: Boolean,
      attribute: 'show-final-sale-text',
    },
    bundleType: {
      type: String,
      attribute: 'bundle-type',
    },
    bundleConfig: {
      type: Object,
      attribute: 'bundle-config',
    },
    customCtaContent: {
      type: Object,
      attribute: 'custom-cta-content',
    },
    description: {
      type: String,
    },
    hideCartControls: {
      type: Boolean,
      attribute: 'hide-cart-controls',
    },
    identifier: {
      type: String,
    },
    discount: {
      type: Number,
    },
    handle: {
      type: String,
    },
    bundleProductImages: {
      type: Array,
    },
    galleryImages: {
      type: Array,
    },
    badges: {
      type: Array,
    },
    _selected: {
      type: Object,
      state: true,
    },
    _selectedQuantity: {
      type: Number,
    },
    loaded: {
      type: Boolean,
      reflect: true,
    },
    _loadingSubmit: {
      state: true,
    },
    lineItems: {
      type: Object,
      attribute: 'line-items',
    },
    variantId: {
      type: String,
      attribute: 'variant-id',
    },
  }

  INITIAL_QUANTITY = 1

  $gallery = createRef()

  get compareAtPrice() {
    if (!Object.keys(this._selected).length) return 0
    return Object.values(this._selected).reduce((acc, variant) => {
      return acc + (variant.compare_at_price || variant.price)
    }, 0)
  }

  get originalPrice() {
    if (!Object.keys(this._selected).length) return 0
    return Object.values(this._selected).reduce((acc, variant) => {
      return acc + variant.price
    }, 0)
  }

  get discountedPrice() {
    if (!Object.keys(this._selected).length) return 0
    const discountedPrice = Object.entries(this._selected).reduce(
      (acc, selectedProduct, index) => {
        const variant = selectedProduct[1]
        const priceWithoutDiscount = variant.compare_at_price || variant.price
        const productLevelDiscount = this.products[index]?.discount
        const discountToApply = this.discount || productLevelDiscount || 0
        return acc + priceWithoutDiscount * (1 - discountToApply / 100)
      },
      0
    )

    return discountedPrice < this.originalPrice
      ? Math.floor(discountedPrice / 100) * 100
      : this.originalPrice
  }

  get atcContext() {
    if (!this.customCtaContent?.context) return null
    const doc = new DOMParser().parseFromString(
      this.customCtaContent.context,
      'text/html'
    )
    if (!doc.documentElement.textContent) return null
    const wrapper = document.createElement('div')
    wrapper.classList.add(
      '[&_p]:mt-0',
      '[&_p]:text-sm',
      '[&_p]:mb-[16px]',
      '[&_p]:font-normal',
      '[&_a]:underline'
    )
    wrapper.innerHTML = doc.documentElement.textContent

    return wrapper
  }

  get finalPrice() {
    return this.discountedPrice < this.compareAtPrice
      ? this.discountedPrice
      : this.compareAtPrice
  }

  get discountPercentage() {
    if (!this.compareAtPrice) return 0
    return Math.floor(
      ((this.compareAtPrice - this.finalPrice) / this.compareAtPrice) * 100
    )
  }

  get _outOfStockItemSelected() {
    return Object.values(this._selected).some((variant) => !variant.available)
  }

  get preOrder() {
    const preOrderProduct = this.products.find(
      (product) => product.preOrder && !!product.preOrderShipDate
    )
    const preOrderVariants = Object.values(this._selected).filter(
      (variant) => variant.preOrder
    )

    let result = false

    if (preOrderVariants.length) {
      const dates = preOrderVariants.reduce((acc, variant) => {
        acc.push(variant.preOrderShipDate)
        return acc
      }, [])
      const latest = Math.max(...dates)
      result = new Date(latest * 1000)
    } else if (preOrderProduct) {
      result = new Date(preOrderProduct.preOrderShipDate)
    }

    if (result)
      result = result.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })

    return result
  }

  constructor() {
    super()
    this.products = []
    this._selected = {}
    this._selectedQuantity = this.INITIAL_QUANTITY
    this.showFinalSaleText = false
    this.bundleProductImages = []
    this.galleryImages = []
    this.badges = []
    this.lineItems = {}
    this.variantId = ''
    this.bundleConfig = {}
    this.customCtaContent = {}
  }

  connectedCallback() {
    super.connectedCallback()
    document.addEventListener('sticky:atc', this._handleSubmit.bind(this))
  }

  disconnectedCallback() {
    super.disconnectedCallback()
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('bundleProductImages')) {
      this.galleryImages = this.bundleProductImages.map((media) => ({
        ...media,
        preview_image: media.preview_image.src,
      }))
    }
    if (changedProperties.has('products')) {
      const selected = this.products
        .filter((product) => !!product.variants?.length)
        .reduce((acc, product, index) => {
          let firstAvailable
          if (product.enabledVariants.length) {
            firstAvailable =
              product.enabledVariants.find((variant) => variant.available) ||
              product.enabledVariants[0]
          } else {
            firstAvailable =
              product.variants.find((variant) => variant.available) ||
              product.variants[0]
          }
          acc[`${product.handle}|${index}`] = firstAvailable
          return acc
        }, {})
      this._selected = selected
      for (const _ of this.products) {
        const firstVariant = _.enabledVariants[0]
        const featuredImage =
          firstVariant?.featured_image?.src || _.featuredImage
        const previewImage =
          firstVariant?.featured_media?.preview_image?.src || _.previewImage
        this.galleryImages.push({
          src: featuredImage,
          alt: _.title,
          preview_image: previewImage,
        })
      }
    }
  }

  firstUpdated() {
    this.loaded = true
  }

  encrypt(num) {
    return num.toString(36)
  }

  _handleSubmit = async (event) => {
    event.preventDefault()
    const uid = crypto.randomUUID()
    this._loadingSubmit = true
    const hasOOSItem = Object.values(this._selected).find(
      (variant) => !variant.available
    )
    if (hasOOSItem) {
      // eslint-disable-next-line no-console
      console.error('Cannot add bundle to cart with out of stock item')
      return // TODO helpful error messaging for user
    }
    // Start Tracking Code
    const trackingItems = Object.values(this._selected)
    const blInfo = {
      name: this.title,
      total: this.finalPrice / 100,
      compare: this.compareAtPrice / 100,
      url: window.location.href,
      id: this.identifier,
      image: `https:${this.galleryImages[0].src}`,
    }
    if (typeof gtmAtcBundle === 'function') {
      // eslint-disable-next-line no-undef
      gtmAtcBundle(trackingItems, blInfo.total) // from gtm-dl-theme.liquid
    }

    if (typeof klAtcBundle === 'function') {
      // eslint-disable-next-line no-undef
      klAtcBundle(
        blInfo.name,
        blInfo.id,
        blInfo.url,
        blInfo.image,
        blInfo.total,
        blInfo.compare
      ) // from klaviyo.liquid
    }

    // End Tracking Code
    await addToCart(
      Object.values(this._selected).map((variant, index) => {
        // Check if the variant has a pre-order ship date, otherwise use the product's, otherwise false
        let preOrderShipDate =
          variant.preOrderShipDate ||
          this.products[index].preOrderShipDate ||
          false
        if (preOrderShipDate)
          preOrderShipDate = new Date(
            preOrderShipDate * 1000
          ).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        const customLineItems =
          (this.lineItems && Object.values(this.lineItems))?.filter(
            (item) => item
          ) || []
        const parsedCustomLineItems = {}
        customLineItems.forEach((item) => {
          parsedCustomLineItems[item.split('::')[0]] = item.split('::')[1]
        })

        return {
          id: variant.id,
          quantity: this._selectedQuantity,
          properties: {
            _bundle_hide_cart_controls: this.hideCartControls,
            _bundle_uid: uid, // ensure we can distinguish between different instances of same bundle
            _bundle_item_key: `${uid}-${index}`, // ensure each line item has a fully unique key
            _bundle: this.handle,
            _bundle_image: this.galleryImages && this.galleryImages[0],
            _bundle_parent_id: this.variantId,
            _Bundle_Name: this.title, // duplicate of legacy property for site/order admin reasons
            _bundle_identifier: this.identifier, // human readable identifier for site/order admin reasons
            _bundle_price: this.finalPrice,
            _bundle_compare_at_price: this.compareAtPrice,
            _bundle_discount_percentage: this.discountPercentage,
            _nVuZGxlX2:
              this.bundleConfig && btoa(JSON.stringify(this.bundleConfig)),
            _j43r28j: this.encrypt(this.discountPercentage),
            // Conditionally include the estimated shipping date if it's a pre-order variant
            ...(preOrderShipDate && {
              'Estimated shipping': preOrderShipDate,
            }),
            _custom_line_items: JSON.stringify(parsedCustomLineItems),
            ...parsedCustomLineItems,
          },
        }
      })
    )
    this._loadingSubmit = false
  }

  _handleVariantChange = (variant, handle, index, preventScroll = false) => {
    const selected = { ...this._selected }
    selected[`${handle}|${index}`] = variant
    this._selected = selected
    this._updateGallery(variant, index, {
      scrollBehavior: preventScroll && 'prevent',
    })
  }

  _handleQuantityChange = (event) => {
    this._selectedQuantity = event.detail.value
  }

  _updateGallery = (variant, index, { scrollBehavior } = {}) => {
    const newImages = [...this.galleryImages]
    const totalBundleProducts = this.products.length
    const bundleImagePosition = newImages.length - totalBundleProducts + index
    if (variant.featured_image) {
      newImages[bundleImagePosition] = {
        src: variant.featured_image?.src,
        alt: variant.featured_image?.alt,
        preview_image: variant.featured_media?.preview_image.src,
      }
      this.galleryImages = newImages
    }

    switch (scrollBehavior) {
      case 'prevent':
        return
      case 'instant':
        this.$gallery.value.setActiveSlideInstant(bundleImagePosition)
        return
      default:
        this.$gallery.value.setActiveSlide(bundleImagePosition)
    }
  }

  _processOptions(options, variants, enabledVariants) {
    // Remove options not present in enabled variants
    let filteredOptions = options
    if (enabledVariants.length) {
      filteredOptions = options.map((option, index) => {
        return {
          ...option,
          values: option.values.filter((value) =>
            enabledVariants.find((variant) => variant.options[index] === value)
          ),
        }
      })
    }
    // Update option order in case variants have been reordered
    const reorderedOptions = filteredOptions.map((option) => {
      const rootValues = enabledVariants.map(
        (variant) => variant[`option${option.position}`]
      )
      return {
        name: option.name,
        values: option.values.sort(
          (a, b) => rootValues.indexOf(a) - rootValues.indexOf(b)
        ),
      }
    })

    return reorderedOptions
  }

  _setEnabledVariants(variants, enabledVariants) {
    if (!enabledVariants?.length) return variants
    const sortedVariants = variants.map((variant) =>
      enabledVariants.find((enabledVariant) => enabledVariant.id === variant.id)
        ? variant
        : { ...variant, available: false }
    )
    return sortedVariants
  }

  _visibleVariantPicker(variants) {
    return variants.length === 1 && variants[0]?.title === 'Default Title'
  }

  render() {
    return html`
      <div id="details" class="form-container">
        <header>
          <ul id="badges">
            ${this.badges.map((badge) => {
              const badgeStyles = {}
              const colorConfig = __THEME_GLOBALS__?.badges[handleize(badge)]
              if (colorConfig) {
                badgeStyles.color = colorConfig.color
                badgeStyles.backgroundColor = colorConfig.backgroundColor
              }
              return html`
                <li class="badge" style=${styleMap(badgeStyles)}>${badge}</li>
              `
            })}
          </ul>
          <slot name="rating"></slot>
          <h1 id="title" class="h1">${this.title}</h1>
        </header>
        <div id="price">
          <span
            class="price-text price-text--compare-at
          ${this.discountedPrice < this.compareAtPrice
              ? ' line-through color-subdued'
              : ''}"
          >
            ${formatMoney(this.compareAtPrice)}
          </span>
          ${this.discountedPrice < this.compareAtPrice
            ? html`
                <span class="price-text price-text--discounted color-on-sale">
                  ${formatMoney(this.discountedPrice)}
                </span>
              `
            : ''}
          ${this.showFinalSaleText
            ? html`
                <span class="price-text price-text--final-sale color-on-sale">
                  Final sale
                </span>
              `
            : ''}
        </div>
      </div>

      <div id="gallery">
        <hh-product-gallery
          ${ref(this.$gallery)}
          .images=${this.galleryImages}
        ></hh-product-gallery>
      </div>

      <div id="form" class="form-container">
        ${this.description
          ? html`<p id="description" class="p">${this.description}</p>`
          : ''}
        <form @submit=${this._handleSubmit}>
          <div class="products">
            ${this.products.map((product, index) => {
              const selectedVariant =
                this._selected[`${product.handle}|${index}`]
              const featuredImage =
                selectedVariant?.featured_image?.src || product.featuredImage
              const showFinalSaleText =
                [true, 'true'].includes(product.finalSale) ||
                [true, 'true'].includes(
                  this._selected[`${product.handle}|${index}`].finalSale
                )
              return html`
                <div class="product">
                  <div class="product__image">
                    <a href=${product.url}>
                      ${featuredImage
                        ? html`<img
                            src=${featuredImage}
                            alt=${product.title}
                          />`
                        : ''}
                    </a>
                  </div>
                  <div class="product__info">
                    <a href=${product.url} class="product__info__title">
                      <h1 class="h6">${product.title}</h1>
                      <span
                        ?hidden=${!showFinalSaleText}
                        class="color-on-sale h7"
                      >
                        Final sale
                      </span>
                    </a>
                    ${product.variants.length
                      ? html`
                          <hh-variant-picker
                            .product=${product}
                            ?hidden=${this._visibleVariantPicker(
                              product.variants
                            )}
                            .variants=${this._setEnabledVariants(
                              product.variants,
                              product.enabledVariants
                            )}
                            .options=${this._processOptions(
                              product.options,
                              product.variants,
                              product.enabledVariants
                            )}
                            .firstSelected=${Object.values(this._selected)[
                              index
                            ]}
                            @changeVariant=${(event) =>
                              this._handleVariantChange(
                                event.detail.variant,
                                event.detail.handle,
                                index,
                                event.detail?.preventScroll
                              )}
                          ></hh-variant-picker>
                        `
                      : ''}
                  </div>
                </div>
              `
            })}
          </div>
          <slot name="shoppay"></slot>

          ${this.customCtaContent
            ? html`<a
                  href="${this.customCtaContent.link}"
                  id="customAtc"
                  class="cart__button !mb-[8px] !my-[8px]
                  flex !text-white gap-[8px] !bg-primary items-center justify-center button"
                >
                  ${this.customCtaContent.image
                    ? html`<img
                        src="${this.customCtaContent.image}"
                        class="w-[30px] h-[30px] object-contain"
                      />`
                    : ''}
                  ${this.customCtaContent.text}
                </a>
                ${this.atcContext ? html`${this.atcContext}` : ''}`
            : html`
                <div id="purchase-controls">
                  <hh-quantity-input
                    initial=${this.INITIAL_QUANTITY}
                    @changeQuantity=${this._handleQuantityChange}
                  >
                  </hh-quantity-input>
                  <button
                    type="submit"
                    class="button purchase-button"
                    ?hidden=${this._outOfStockItemSelected}
                    ?loading=${this._loadingSubmit}
                  >
                    <span class="purchase-button__label">
                      Add to cart
                      ${this.preOrder ? html` - Ships ${this.preOrder}` : ''}
                    </span>
                  </button>
                  <button
                    class="button button--outlined purchase-button"
                    ?hidden=${!this._outOfStockItemSelected}
                    disabled
                    type="submit"
                  >
                    Out of Stock
                  </button>
                </div>
              `}
        </form>
        <slot name="info"></slot>
      </div>
    `
  }

  static styles = [
    styles.reset,
    styles.a11y,
    styles.flex,
    styles.typography,
    styles.skeleton,
    styles.color,
    styles.animation,
    styles.button,
    css`
      .h1,
      .h6,
      .h7,
      .p {
        margin: 0;
      }
      :host {
        display: grid;
        grid-template-columns: 1fr;
        margin: 0 auto;
        max-width: 1140px;
        padding: 14px 0 49px;
        row-gap: 16px;
      }
      .form-container {
        max-width: 548px;
        margin: auto;
        padding: 0 16px;
      }
      .form-container.hide {
        display: none;
      }
      #gallery,
      #details,
      #form {
        width: 100%;
      }
      #details {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      #description {
        margin-top: 26px;
        margin-bottom: 30px;
      }
      @media (min-width: 768px) {
        :host {
          grid-template-columns: 1fr 1fr;
          column-gap: 87px;
          row-gap: 0;
        }
        .form-container {
          padding: 0;
        }
        #gallery {
          grid-column: 1 / 2;
          grid-row: 1 / 3;
        }
        #details {
          grid-column: 2 / 3;
          grid-row: 1;
          gap: 20px;
        }
        #description {
          margin-top: 20px;
          margin-bottom: 40px;
        }
        #form {
          grid-column: 2 / 3;
          grid-row: 2;
        }
      }
      #badges {
        display: flex;
        flex-wrap: wrap;
        margin-bottom: 15px;
        gap: 10px;
      }
      #badges:empty {
        display: none;
      }
      .badge {
        font-size: 12px;
        padding: 1px 15px;
        border-radius: 4px;
        background-color: #fff;
      }
      @media screen and (min-height: 830px) and (min-width: 992px) {
        :host {
          max-width: 1440px;
          padding: 17px 32px 49px;
        }
      }
      #title {
        margin-top: 15px;
      }
      #price {
        align-items: center;
        display: flex;
      }
      .price-text {
        font-family: 'PP Right Grotesk', sans-serif;
        font-size: 26px;
        font-weight: 400;
      }
      .price-text--final-sale {
        font-size: 18px;
      }
      .price-text + .price-text {
        margin-left: 10px;
      }
      .price-text + .price-text--final-sale {
        margin-left: 15px;
      }
      .products {
        display: flex;
        flex-direction: column;
        max-width: 442px;
      }
      .product {
        border-bottom: solid 1px var(--color-neutral);
        display: flex;
        padding: 20px 0;
        gap: 9px;
      }
      .product__image {
        width: 82px;
      }
      .product__image a,
      .product__image img {
        width: 100%;
      }
      .product__info {
        width: calc(100% - 82px);
        display: flex;
        flex-direction: column;
        gap: 9px;
      }
      .product__info__title {
        display: flex;
        gap: 10px;
        align-items: center;
      }
      #purchase-controls {
        display: flex;
        gap: 18px;
        height: 56px;
        margin-top: 16px;
      }
      hh-quantity-input {
        --height: 100%;
        --background: white;
        --border-color: var(--color-neutral);
        --width: 100px;
        --padding: 0 15px;
        --button-width: 14px;
      }
      .purchase-button {
        height: 100%;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .purchase-button[hidden] {
        display: none;
      }
      .purchase-button__label {
        font: inherit;
      }
      [loading] > .purchase-button__label {
        display: none;
      }
      .purchase-button[loading] {
        pointer-events: none;
      }
      .purchase-button[loading]::after {
        content: '';
        display: block;
        width: 14px;
        height: 14px;
        border: 2px solid var(--color-neutral);
        border-left-color: var(--color-black);
        border-radius: 50%;
        animation: spin 0.5s infinite linear;
      }
    `,
  ]
}

customElements.define('hh-product-bundle-pdp', HHProductBundlePDP)
